<script setup lang="ts">
  import { type RuleExpression, useField } from 'vee-validate'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { defineProps, defineEmits, type PropType, computed, ref, type Component } from 'vue'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import {
    Mail,
    Phone,
    Calendar,
    Clock,
    Link,
    Hash,
    Type,
    User,
    ArrowLeftRight
  } from 'lucide-vue-next'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const themeColor = computed(() => useThemeColor().value)

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [String, Number, null] as PropType<string | number | null>,
      required: false
    },
    type: {
      type: String,
      required: false,
      default: 'text'
    },
    step: {
      type: String,
      required: false
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    error: {
      type: String,
      required: false
    },
    class: {
      type: String,
      required: false,
      default: ''
    },
    suffix: {
      type: String,
      required: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    asterisk: {
      type: Boolean,
      required: false,
      default: false
    },
    aiSource: {
      type: Boolean,
      required: false,
      default: false
    },
    showIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    dataCy: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits([
    'update:modelValue',
    'input',
    'blur',
    'focus',
    'keydown',
    'validation-state',
    'filesChanged'
  ])

  const {
    value,
    errorMessage: internalErrorMessage,
    handleBlur,
    handleChange,
    meta,
    errors
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const touched = ref(false)

  const errorMessage = computed(() => {
    if (props.error) {
      return props.error
    }
    return touched.value ? internalErrorMessage.value : ''
  })

  const inputIcon = computed(() => {
    switch (props.type) {
      case 'email':
        return Mail
      case 'tel':
      case 'phone':
        return Phone
      case 'date':
        return Calendar
      case 'time':
        return Clock
      case 'url':
        return Link
      case 'number':
        return Hash
      default:
        return Type
    }
  })

  const showIcon = computed(() => {
    return (
      props.showIcon &&
      ['email', 'tel', 'phone', 'date', 'time', 'url', 'number'].includes(props.type)
    )
  })

  const emitInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    const value =
      props.type === 'number' ? (target.value === '' ? null : Number(target.value)) : target.value
    emit('input', value)
    emit('update:modelValue', value)
    handleChange(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
  }

  const emitChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    const value =
      props.type === 'number' ? (target.value === '' ? null : Number(target.value)) : target.value
    emit('update:modelValue', value)
    handleChange(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
  }

  const handleInputBlur = (event: Event) => {
    touched.value = true
    handleBlur(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
    emit('blur', event)
  }

  const handleInputFocus = (event: FocusEvent) => {
    touched.value = true
    emit('focus', event)
  }

  const handleKeydown = (event: KeyboardEvent) => {
    emit('keydown', event)
  }
</script>

<template>
  <label
    v-if="label"
    class="mb-0 flex cursor-auto items-center text-sm font-medium"
  >
    <span class="truncate">{{ label }}</span>
    <span
      v-if="asterisk"
      class="text-base font-normal text-red-500"
    >
      *
    </span>
    <span
      v-if="aiSource"
      class="inline-flex h-5 w-5 items-center rounded bg-purple-200 p-1 text-xs font-semibold text-purple-700"
    >
      KI
    </span>
    <TooltipProvider v-if="$slots.tooltipContent && $slots.tooltipTrigger">
      <Tooltip>
        <TooltipTrigger>
          <span
            class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600
              transition-colors hover:bg-gray-200"
          >
            <slot name="tooltipTrigger" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <slot name="tooltipContent" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </label>
  <div :class="errorMessage || suffix || showIcon ? 'relative' : ''">
    <input
      :id="props.id"
      v-model="value"
      class="block w-full rounded-lg px-3 py-2 text-sm text-gray-800 placeholder:text-gray-500 focus:z-10"
      :name="props.name"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :title="title"
      :class="[
        errorMessage
          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
          : `border-gray-200 focus:border-${themeColor}-600 focus:ring-${themeColor}-600`,
        disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-text bg-white',
        suffix ? 'pr-10' : '',
        showIcon ? 'pl-10' : '',
        errorMessage ? 'pr-10' : '',
        props.class
      ]"
      :data-cy="dataCy"
      @input="emitInput"
      @change="emitChange"
      @blur="handleInputBlur"
      @focus="handleInputFocus"
      @keydown="handleKeydown"
    />

    <div
      v-if="showIcon"
      class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-gray-400"
    >
      <component
        :is="inputIcon"
        class="h-4 w-4"
      />
    </div>

    <div
      v-if="suffix"
      class="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3 text-base font-light
        text-gray-500"
    >
      {{ suffix }}
    </div>
    <div
      v-if="errorMessage"
      class="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3"
      :class="{ 'opacity-50': disabled }"
    >
      <ExclamationCircleIcon class="size-5 flex-shrink-0 text-red-500" />
    </div>
  </div>
  <p
    v-if="errorMessage"
    class="mt-1 text-xs text-red-600"
    data-cy="input-error-message"
    :class="{ 'opacity-50': disabled }"
  >
    {{ errorMessage }}
  </p>
</template>
